import { SHKIA_LABEL, withSunset } from '@app/luach'
import {
  say,
  type BoardWidget as Widget,
  type ZmanimTurn,
  zmanimDay,
} from '@app/shared'
import { useQuery } from '@tanstack/react-query'
import type { OrgListItem } from '../../lib/org'
import { orgHref } from '../../lib/public-router'
import { useTRPC } from '../../lib/trpc'
import { htmlPageFrameSandbox } from '../../ui/formats/HtmlDocView'
import { Said } from '../../ui/Said'
import { kioskHref } from '../kiosk'
import { nightOf } from '../public-schedule'
import { BOARD_REREAD_MS, type Leading } from './board-moment'

/**
 * A time as a board sets it: the figure large, the meridiem set back. The
 * figure is the server's, exactly as the sheet prints it.
 */
function Time({ value }: { value: string }) {
  const m = /^(\d{1,2}:\d{2})\s*(AM|PM)$/i.exec(value.trim())
  if (!m) return <span className="display-board__figure">{value}</span>
  return (
    <span className="display-board__figure">
      {m[1]}
      <span className="display-board__meridiem">{m[2]}</span>
    </span>
  )
}

/**
 * The clock: the hour at the shul, and the day it is there — civil and Hebrew.
 * The hour runs on the screen's own clock in the shul's zone; the day is the
 * server's, so it turns where the shul's day turns.
 */
export function BoardClock({
  shul,
  now,
  leading,
  face,
}: {
  shul: OrgListItem
  now: number
  leading: Leading | null
  face: 'digital' | 'analog'
}) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: shul.timeZoneId,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now)
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value)
  const [hour, minute, second] = [get('hour'), get('minute'), get('second')]
  const civil = new Intl.DateTimeFormat('en-US', {
    timeZone: shul.timeZoneId,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(now)
  return (
    <div className="display-board-clock" data-face={face}>
      {face === 'analog' ? (
        <AnalogFace hour={hour} minute={minute} second={second} />
      ) : (
        <div className="display-board-clock__time">
          {hour % 12 || 12}:{String(minute).padStart(2, '0')}
        </div>
      )}
      <div className="display-board-clock__dates">
        <span>{civil}</span>
        {leading ? (
          <span className="display-board-clock__he" lang="he" dir="rtl">
            {leading.today.hebrewDateHe}
          </span>
        ) : null}
      </div>
    </div>
  )
}

/**
 * A round face: twelve hour marks, the hour and minute hands, and a fine
 * seconds hand, drawn on a 100-unit square so it scales to whatever room the
 * clock is given. The hands sweep with the minute rather than jumping on it.
 */
function AnalogFace({
  hour,
  minute,
  second,
}: {
  hour: number
  minute: number
  second: number
}) {
  const hand = (turns: number, length: number, className: string) => {
    const angle = turns * 2 * Math.PI
    return (
      <line
        className={className}
        x1={50}
        y1={50}
        x2={50 + length * Math.sin(angle)}
        y2={50 - length * Math.cos(angle)}
      />
    )
  }
  return (
    <svg
      className="display-board-clock__face"
      viewBox="0 0 100 100"
      role="img"
      aria-label={`${hour % 12 || 12}:${String(minute).padStart(2, '0')}`}
    >
      <circle className="display-board-clock__rim" cx={50} cy={50} r={48} />
      {Array.from({ length: 12 }, (_, i) => {
        const angle = (i / 12) * 2 * Math.PI
        const inner = i % 3 === 0 ? 38 : 41
        return (
          <line
            key={angle}
            className="display-board-clock__mark"
            x1={50 + inner * Math.sin(angle)}
            y1={50 - inner * Math.cos(angle)}
            x2={50 + 45 * Math.sin(angle)}
            y2={50 - 45 * Math.cos(angle)}
          />
        )
      })}
      {hand(((hour % 12) + minute / 60) / 12, 24, 'display-board-clock__hour')}
      {hand((minute + second / 60) / 60, 36, 'display-board-clock__minute')}
      {hand(second / 60, 40, 'display-board-clock__second')}
      <circle className="display-board-clock__pin" cx={50} cy={50} r={1.6} />
    </svg>
  )
}

export function BoardWidget({
  widget,
  shul,
  leading,
  orgShortId,
  boardShortId,
  onDate,
  online,
  onGive,
}: {
  widget: Widget
  shul: OrgListItem
  leading: Leading | null
  orgShortId: string
  boardShortId: string | null
  onDate: string | null
  online: boolean
  onGive: (
    target: string,
    choose: boolean,
    title: 'Give' | 'Pay my account',
  ) => void
}) {
  if (widget.kind === 'page') {
    if (!boardShortId || !onDate) return null
    const src = `/api/board-screen-page/${encodeURIComponent(orgShortId)}/${encodeURIComponent(boardShortId)}/${encodeURIComponent(widget.key)}?on=${encodeURIComponent(onDate)}`
    return (
      <iframe
        className="display-board-page"
        title="Board page"
        sandbox={htmlPageFrameSandbox('live-data')}
        referrerPolicy="no-referrer"
        src={src}
      />
    )
  }
  if (widget.kind === 'announcement')
    return (
      <div className="display-board-note">
        <h2 className="display-board-note__title" dir="auto">
          {widget.title}
        </h2>
        {widget.body ? (
          <p className="display-board-note__body" dir="auto">
            {widget.body}
          </p>
        ) : null}
      </div>
    )
  if (widget.kind === 'give')
    return (
      <GiveWidget
        widget={widget}
        shul={shul}
        orgShortId={orgShortId}
        online={online}
        onGive={onGive}
      />
    )
  if (widget.kind === 'pay')
    return (
      <button
        type="button"
        className="display-board-give"
        disabled={!online}
        onClick={() =>
          onGive(
            kioskHref(orgHref(orgShortId, 'pay'), true),
            false,
            'Pay my account',
          )
        }
      >
        <span className="display-board-give__action">Pay my account</span>
        <span className="display-board-give__name">
          {online ? shul.name : 'Paying needs a connection'}
        </span>
      </button>
    )
  if (!leading) return null
  if (widget.kind === 'zmanim')
    return <Zmanim shul={shul} leading={leading} turns={widget.turns} />
  return <Schedule shul={shul} leading={leading} />
}

function GiveWidget({
  widget,
  shul,
  orgShortId,
  online,
  onGive,
}: {
  widget: Extract<Widget, { kind: 'give' }>
  shul: OrgListItem
  orgShortId: string
  online: boolean
  onGive: (
    target: string,
    choose: boolean,
    title: 'Give' | 'Pay my account',
  ) => void
}) {
  const trpc = useTRPC()
  const campaignId =
    widget.target.kind === 'campaign' ? widget.target.campaignShortId : ''
  const campaign = useQuery({
    ...trpc.donation.campaign.queryOptions({
      shortId: orgShortId,
      campaignShortId: campaignId,
    }),
    enabled: campaignId !== '',
    refetchInterval: BOARD_REREAD_MS,
  })
  const href =
    widget.target.kind === 'campaign'
      ? kioskHref(orgHref(orgShortId, 'donate', campaignId), true)
      : kioskHref(orgHref(orgShortId, 'donate'), true)
  const name =
    widget.target.kind === 'campaign' ? campaign.data?.label : shul.name
  const open =
    online &&
    (widget.target.kind !== 'campaign' || (!campaign.isError && !!name))
  return (
    <button
      type="button"
      className="display-board-give"
      disabled={!open}
      onClick={() => onGive(href, true, 'Give')}
    >
      <span className="display-board-give__action">Give</span>
      <span className="display-board-give__name">
        {!online
          ? 'Giving needs a connection'
          : open
            ? name
            : 'Giving is not open'}
      </span>
    </button>
  )
}

/**
 * The day's zmanim — the ones the shul ticked for its home page — as a list. In
 * a zone much wider than it is tall the list runs across instead of down. Once
 * the entry's turn has passed it shows tomorrow's, said as tomorrow's.
 */
function Zmanim({
  shul,
  leading,
  turns,
}: {
  shul: OrgListItem
  leading: Leading
  turns: ZmanimTurn | undefined
}) {
  const band = leading.band
  if (!band) return null
  const now = Date.parse(band.now.at)
  const tomorrow =
    zmanimDay(turns, band.turns, now) === 'tomorrow' && band.tomorrow.length > 0
  const entries = tomorrow ? band.tomorrow : band.entries
  return (
    <div className="display-board-zmanim">
      <h2 className="display-board__heading">
        <Said
          said={
            tomorrow
              ? say(shul.publicLanguage.zmanim, "Tomorrow's zmanim", 'זמני מחר')
              : say(shul.publicLanguage.zmanim, 'Zmanim', 'זמנים')
          }
        />
      </h2>
      <ul className="display-board-zmanim__list">
        {entries.map((e) => (
          <li
            key={e.key}
            className="display-board-zmanim__row"
            data-active={
              e.times.every((t) => Date.parse(t.at) <= now) ? undefined : true
            }
          >
            <span className="display-board-zmanim__label">
              <Said
                said={say(shul.publicLanguage.zmanim, e.labelEn, e.labelHe)}
              />
            </span>
            <span className="display-board-zmanim__times">
              {e.times.map((t) => (
                <Time key={t.key} value={t.time} />
              ))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The occasion the shul's home page leads with, as its own sheet prints it:
 * each day of the opening run, and its lines.
 */
function Schedule({ shul, leading }: { shul: OrgListItem; leading: Leading }) {
  const lead = leading.lead
  if (!lead) return null
  const lang = shul.publicLanguage
  return (
    <div className="display-board-schedule">
      <h2 className="display-board__heading">
        <Said said={say(lang.occasions, lead.labelEn, lead.labelHe)} />
      </h2>
      {lead.candleLighting && lead.state !== 'during' ? (
        <p className="display-board-schedule__candles">
          <Said said={say(lang.lines, 'Candle lighting', 'הדלקת נרות')} />{' '}
          <Time value={lead.candleLighting.time} />
        </p>
      ) : null}
      <div className="display-board-schedule__days">
        {lead.days.map((day) => (
          <section key={day.date} className="display-board-schedule__day">
            {lead.days.length > 1 ? (
              <h3 className="display-board-schedule__dayname">
                <Said
                  said={say(lang.occasions, day.dayLabelEn, day.dayLabelHe)}
                />
              </h3>
            ) : null}
            <ul className="display-board-schedule__lines">
              {withSunset(day).map((item) => {
                if (item.kind === 'anchor')
                  return (
                    <li key="shkia" className="display-board-schedule__line">
                      <span className="display-board-schedule__label">
                        <Said
                          said={say(lang.lines, SHKIA_LABEL.en, SHKIA_LABEL.he)}
                        />
                      </span>
                      <span className="display-board-schedule__time">
                        <Time value={item.time} />
                      </span>
                    </li>
                  )
                const e = item.entry
                const night = lang.lines === 'he' ? null : nightOf(day, e)
                return (
                  <li
                    key={`${e.lineId}-${e.dayOffset}`}
                    className="display-board-schedule__line"
                  >
                    <span className="display-board-schedule__label">
                      {night ? (
                        <span className="display-board-schedule__night">
                          {night}{' '}
                        </span>
                      ) : null}
                      <Said said={say(lang.lines, e.labelEn, e.labelHe)} />
                    </span>
                    <span className="display-board-schedule__time">
                      {e.displayText ? (
                        <span dir="auto">{e.displayText}</span>
                      ) : e.time ? (
                        <Time value={e.time} />
                      ) : e.parts?.length ? (
                        e.parts.map((p) =>
                          p.time ? <Time key={p.key} value={p.time} /> : null,
                        )
                      ) : null}
                    </span>
                  </li>
                )
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}
